import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Loader2, Lock } from "lucide-react";
import { parseDotenv, serializeDotenv } from "@open-frontend/shared/dotenv";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface RevealedVar {
  key: string;
  value: string;
  isSecret: boolean;
}

interface SaveResult {
  created: number;
  updated: number;
  deleted: number;
  warnings: string[];
}

/**
 * Edit every base var of one environment as .env text. Values are fetched
 * decrypted (reauth-gated, like the per-row reveal) and held only in this
 * component's state — never in the query cache. Saving sends just the diff
 * against what was loaded: changed/added keys plus an explicit delete list.
 */
export function BulkEnvEditor({
  orgSlug,
  projectSlug,
  environment,
  hasCustomOverrides,
  requestReauth,
  onDirtyChange,
  onClose,
}: {
  orgSlug: string;
  projectSlug: string;
  environment: string;
  /** Custom-environment overrides exist for this environment (not editable here). */
  hasCustomOverrides: boolean;
  /** Ask the page to run its reauth prompt, then call `retry`. */
  requestReauth: (retry: () => void) => void;
  onDirtyChange: (dirty: boolean) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const base = `/orgs/${orgSlug}/projects/${projectSlug}/env-vars`;
  const [loaded, setLoaded] = useState<RevealedVar[] | null>(null);
  const [text, setText] = useState("");
  const [locked, setLocked] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLocked(false);
    setLoadError(null);
    try {
      const r = await api.post<{ vars: RevealedVar[] }>(
        `${base}/bulk-reveal`,
        { environment },
        { headers: { "x-reauth": "yes" } },
      );
      setLoaded(r.vars);
      setText(serializeDotenv(r.vars));
    } catch (e) {
      if (e instanceof ApiError && e.code === "reauth_required") {
        setLocked(true);
        requestReauth(() => void load());
      } else {
        setLoadError(e instanceof ApiError ? e.message : "failed to load variables");
      }
    }
    // requestReauth is deliberately not a dep: it is a fresh closure each
    // render and the request only depends on the target environment.
  }, [base, environment]);

  useEffect(() => {
    void load();
  }, [load]);

  const parsed = useMemo(() => parseDotenv(text), [text]);
  const diff = useMemo(() => {
    const before = new Map((loaded ?? []).map((v) => [v.key, v.value]));
    const after = new Map(parsed.entries.map((e) => [e.key, e.value]));
    return {
      added: parsed.entries.filter((e) => !before.has(e.key)),
      changed: parsed.entries.filter((e) => before.has(e.key) && before.get(e.key) !== e.value),
      removed: [...before.keys()].filter((k) => !after.has(k)),
    };
  }, [loaded, parsed]);
  const changeCount = diff.added.length + diff.changed.length + diff.removed.length;
  const dirty = loaded !== null && changeCount > 0;

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  const save = useMutation({
    mutationFn: () =>
      api.post<SaveResult>(`${base}/bulk-edit`, {
        environment,
        set: [...diff.added, ...diff.changed],
        deleteKeys: diff.removed,
      }),
    onSuccess: () => {
      // What was just written becomes the new baseline; secret flags carry over.
      const flags = new Map((loaded ?? []).map((v) => [v.key, v.isSecret]));
      setLoaded(parsed.entries.map((e) => ({ ...e, isSecret: flags.get(e.key) ?? true })));
      qc.invalidateQueries({ queryKey: ["env-vars", orgSlug, projectSlug] });
      qc.invalidateQueries({ queryKey: ["project", orgSlug, projectSlug] });
    },
  });
  const canSave = dirty && parsed.errors.length === 0 && !save.isPending;

  if (loaded === null) {
    return (
      <div className="px-4 py-10 text-center text-sm text-muted-foreground space-y-3">
        {loadError ? (
          <div className="text-danger">{loadError}</div>
        ) : locked ? (
          <div>
            Confirm your password to view <span className="font-mono">{environment}</span> values.
          </div>
        ) : (
          <div className="inline-flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        )}
        <div className="flex justify-center gap-2">
          <Button variant="default" size="sm" onClick={onClose}>Cancel</Button>
          {(locked || loadError) && (
            <Button variant="primary" size="sm" onClick={() => void load()}>
              <Lock className="h-3.5 w-3.5" /> {locked ? "Unlock" : "Retry"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 space-y-2">
      <div className="text-xs text-muted-foreground">
        All <span className="font-mono">{environment}</span> variables as <span className="font-mono">KEY=value</span>{" "}
        lines. Removing a line deletes that variable from this environment only; new variables are saved as secret.
        {hasCustomOverrides && " Custom-environment overrides are not shown here and are left untouched."}
      </div>
      <textarea
        aria-label={`${environment} environment variables`}
        value={text}
        onChange={(e) => { setText(e.target.value); if (save.isSuccess || save.isError) save.reset(); }}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "s") {
            e.preventDefault();
            if (canSave) save.mutate();
          }
        }}
        spellCheck={false}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        wrap="off"
        autoFocus
        placeholder={"DATABASE_URL=postgres://…\nAPI_KEY=sk-…"}
        className="w-full min-h-[320px] max-h-[70vh] font-mono text-xs leading-5 bg-subtle border border-border rounded-md p-2.5 resize-y whitespace-pre overflow-auto"
      />
      {parsed.errors.length > 0 && (
        <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5 space-y-0.5">
          {parsed.errors.slice(0, 8).map((e) => (
            <div key={`${e.line}-${e.message}`} className="flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Line {e.line}: {e.message}</span>
            </div>
          ))}
          {parsed.errors.length > 8 && <div>…and {parsed.errors.length - 8} more</div>}
        </div>
      )}
      {save.error && (
        <div className="text-xs text-danger">
          {save.error instanceof ApiError ? save.error.message : "save failed"}
        </div>
      )}
      {save.data?.warnings.map((w) => (
        <div
          key={w}
          className="text-xs text-attention bg-attention-subtle border border-attention/30 rounded-md px-2.5 py-1.5 flex items-start gap-1.5"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{w}</span>
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-mono min-w-0">
          {dirty ? (
            <span className="flex flex-wrap gap-x-3 gap-y-0.5">
              {diff.added.length > 0 && (
                <span className="text-success" title={diff.added.map((e) => e.key).join(", ")}>
                  +{diff.added.length} added
                </span>
              )}
              {diff.changed.length > 0 && (
                <span className="text-attention" title={diff.changed.map((e) => e.key).join(", ")}>
                  ~{diff.changed.length} changed
                </span>
              )}
              {diff.removed.length > 0 && (
                <span className="text-danger break-all">
                  −{diff.removed.length} removed: {diff.removed.join(", ")}
                </span>
              )}
            </span>
          ) : save.data ? (
            <span className="text-success inline-flex items-center gap-1">
              <Check className="h-3.5 w-3.5" /> Saved — redeploy to apply.
            </span>
          ) : (
            <span className="text-subtle-foreground">No changes</span>
          )}
        </div>
        <div className="flex gap-2 ml-auto">
          <Button variant="default" size="sm" onClick={onClose} disabled={save.isPending}>
            {dirty ? "Discard" : "Close"}
          </Button>
          <Button variant="primary" size="sm" onClick={() => save.mutate()} disabled={!canSave}>
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Save{changeCount > 0 ? ` ${changeCount} change${changeCount === 1 ? "" : "s"}` : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}
